import { CidCvnCheckResult, CidCvnReportResponse } from '@packages/models/api/cid-cvn';
import { CidCvnPdfOptions } from './cid-cvn.pdf';
import { DATE_FORMAT } from '@web/config/constants';
import { getDisplayValue, getDisplayDate } from '@web/utils';
import type { UseTranslationResponse } from 'react-i18next';

const PDF_TITLE = 'CID CVN Report';

const resultMessageMap: Record<CidCvnCheckResult, string> = {
    [CidCvnCheckResult.Pass]: 'cidCvnCheck:report.resultMessage.pass',
    [CidCvnCheckResult.Fail]: 'cidCvnCheck:report.resultMessage.fail',
    [CidCvnCheckResult.NoMatchFound]: 'cidCvnCheck:report.resultMessage.noMatchFound',
};

type TranslateFn = UseTranslationResponse<string, undefined>['t'];

export function getCidCvnResultMessages(result: CidCvnCheckResult, createdDate: string, t: TranslateFn) {
    if (!result || !(result in resultMessageMap)) {
        return {
            resultMessage: '',
            resultMessageReasons: [],
        };
    }
    const resultMessage = t(resultMessageMap[result], { createdDate });
    const reasons =
        result === CidCvnCheckResult.NoMatchFound
            ? t('cidCvnCheck:report.resultMessage.noMatchFoundReasons', { returnObjects: true })
            : [];

    return {
        resultMessage,
        resultMessageReasons: Array.isArray(reasons) ? reasons : [],
    };
}

export function getCidCvnReportMetaDetails(
    vin: string,
    reportName: string,
    createdTimestamp: string,
    createdAccountName: string
): { label: string; value: string }[] {
    return [
        { label: 'VIN ', value: getDisplayValue(vin) },
        { label: 'Report Name', value: getDisplayValue(reportName) },
        { label: 'Date Image Was Checked', value: getDisplayDate(createdTimestamp, DATE_FORMAT.MMM_D_YEAR) },
        { label: 'User Name', value: getDisplayValue(createdAccountName) },
    ];
}

export function getCidCvnPdfFileName(vin: string, reportName: string): string {
    const sanitize = (text: string) =>
        text
            .trim()
            .replace(/[\\:*?"<>|]/g, '')
            .replace(/[\s/]+/g, '_');
    const sanitizedReportName = sanitize(reportName);
    const sanitizedVin = sanitize(vin);
    return `${sanitizedVin}_${sanitizedReportName}`;
}

export function getCidCvnPdfOptions(data: CidCvnReportResponse, t: TranslateFn): CidCvnPdfOptions | undefined {
    if (!data) {
        return undefined;
    }

    const { canViewDiagnosticInfo, result, createdTimestamp, reportName, vin } = data;
    const cid = canViewDiagnosticInfo ? data.cid : '';
    const cvn = canViewDiagnosticInfo ? data.cvn : '';
    const createdAccountName = canViewDiagnosticInfo ? data.createdAccountName : 'Subaru Technician';

    const createdDate = getDisplayDate(createdTimestamp, DATE_FORMAT.MON_DAY_YEAR_AT_H_MM);
    const { resultMessage, resultMessageReasons } = getCidCvnResultMessages(result, createdDate, t);
    const metaDetails = getCidCvnReportMetaDetails(vin, reportName, createdTimestamp, createdAccountName);
    const fileName = getCidCvnPdfFileName(vin, reportName);

    return {
        canViewDiagnosticInfo,
        title: PDF_TITLE,
        result,
        cid: getDisplayValue(cid),
        cvn: getDisplayValue(cvn),
        metaDetails,
        fileName,
        resultMessage,
        resultMessageReasons,
    };
}
